import { Fragment, useMemo } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { Check, ChevronDown } from "untitledui-js-base";
import { useAuthContext } from "providers/Auth";

const getOptions = (
  optInDays: number,
  optOutDays: number,
  defaultEnrollmentSubText: string
) => [
  ...(defaultEnrollmentSubText
    ? [
        {
          label: "Default enrollment type",
          subText: defaultEnrollmentSubText,
          value: "is_default_enrollment"
        }
      ]
    : []),
  {
    label: `Renters can opt-in within ${optInDays} day${optInDays > 1 ? "s" : ""}`,
    subText: `Renters are notified via email and have ${optInDays} day${optInDays > 1 ? "s" : ""} to opt in`,
    value: "opt_in"
  },
  {
    label: `Renters can opt-out within ${optOutDays} day${optOutDays > 1 ? "s" : ""}`,
    subText: `Renters are notified via email and have ${optOutDays} day${optOutDays > 1 ? "s" : ""} to opt out`,
    value: "opt_out"
  },
  {
    label: "Required",
    subText:
      "Renters are enrolled with no option to opt out or unenroll using the app. You can unenroll them in the Boom platform",
    value: "direct_enrollment"
  }
];

export const ListBox = ({
  value,
  setValue,
  disabled,
  defaultEnrollmentSubText
}: {
  value: {
    opt_in: boolean;
    opt_out: boolean;
    is_default_enrollment: boolean;
  };
  disabled?: boolean;
  setValue: (value: {
    opt_in: boolean;
    opt_out: boolean;
    is_default_enrollment: boolean;
  }) => void;
  defaultEnrollmentSubText: string;
}) => {
  const { auth } = useAuthContext();

  const optIn = auth?.partner.opt_in_period_days || 30,
    optOut = auth?.partner.opt_out_period_days || 7;

  const type = useMemo(() => {
    if (value.opt_in) return "opt_in";
    if (value.opt_out) return "opt_out";
    if (value.is_default_enrollment) return "is_default_enrollment";
    return "direct_enrollment";
  }, [value]);

  const options = useMemo(
    () => getOptions(optIn, optOut, defaultEnrollmentSubText),
    [optIn, optOut, defaultEnrollmentSubText]
  );
  const selected = options.find((opt) => opt.value === type);

  return (
    <Listbox
      as="div"
      value={selected?.value}
      disabled={disabled}
      onChange={(v) => {
        if (v === "opt_in")
          setValue({
            opt_in: true,
            opt_out: false,
            is_default_enrollment: false
          });
        else if (v === "opt_out")
          setValue({
            opt_in: false,
            opt_out: true,
            is_default_enrollment: false
          });
        else if (v === "is_default_enrollment")
          setValue({
            opt_in: false,
            opt_out: false,
            is_default_enrollment: true
          });
        else
          setValue({
            opt_in: false,
            opt_out: false,
            is_default_enrollment: false
          });
      }}
    >
      <div className="relative mt-1 min-w-[300px]">
        <Listbox.Button className="relative cursor-pointer flex gap-2 items-center justify-between h-10 px-3.5 py-2.5  focus:border-primary-100 hover:border-primary-100 shadow-xs hover:shadow-focus-ring focus:shadow-focus-ring border-alpha-100 mb-2 w-full rounded-md border text-base font-medium focus:outline-0 focus:ring-0 disabled:bg-alpha-40 disabled:hover:border-alpha-100 disabled:hover:shadow-xs">
          <span className="block test-sm truncate font-medium text-content-1000">
            {selected?.label}
          </span>
          <ChevronDown className="text-black" size="16px" aria-hidden="true" />
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute right-0 bg-white z-[4]  mt-3  w-[444px] border border-alpha-100 shadow-xl rounded-lg">
            {options.map((opt, personIdx) => (
              <Listbox.Option
                key={personIdx}
                className={({ active }) =>
                  `relative cursor-pointer border-b border-alpha-100 last-of-type:border-b-0 select-none px-6 py-4 ${
                    active ? "bg-alpha-40" : "text-gray-900"
                  }`
                }
                value={opt.value}
              >
                {({ selected }) => (
                  <div className="flex gap-2 items-center justify-between">
                    <div>
                      <p className="text-base text-content-1000 font-medium">
                        {opt.label}
                      </p>
                      <p className=" text-sm text-content-400">{opt.subText}</p>
                    </div>
                    {selected ? (
                      <Check
                        className=" stroke-content-500"
                        aria-hidden="true"
                        size="20px"
                      />
                    ) : null}
                  </div>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
};
